import type { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo } from "react";
import { useLocalStorage } from "usehooks-ts";

type ColumnMeta = {
  displayName?: string;
  defaultVisible?: boolean;
};

/**
 * Generic hook to manage column visibility state with localStorage persistence
 * Works with any table data type and columns
 */
export const useColumnVisibility = <TData = unknown>(
  columns: ColumnDef<TData>[],
  storageKey: string
): [
  VisibilityState,
  (
    value: VisibilityState | ((val: VisibilityState) => VisibilityState)
  ) => void,
] => {
  // Generate default visibility state from column definitions
  const defaultVisibility = useMemo(() => {
    const visibility: VisibilityState = {};

    for (const column of columns) {
      if (column.id && column.enableHiding !== false) {
        // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
        const meta = column.meta as ColumnMeta | undefined;
        visibility[column.id] = meta?.defaultVisible ?? true;
      }
    }

    return visibility;
  }, [columns]);

  // Use localStorage to persist column visibility preferences
  const [columnVisibility, setColumnVisibility] =
    useLocalStorage<VisibilityState>(storageKey, defaultVisibility);

  return [columnVisibility, setColumnVisibility];
};

/**
 * Helper to get column display names from column definitions
 * Falls back to formatted column.id if no displayName is provided
 */
export const getColumnDisplayNames = <TData = unknown>(
  columns: ColumnDef<TData>[]
): Record<string, string> => {
  const displayNames: Record<string, string> = {};

  for (const column of columns) {
    if (column.id) {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const meta = column.meta as ColumnMeta | undefined;
      displayNames[column.id] =
        meta?.displayName ||
        column.id.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    }
  }

  return displayNames;
};
